import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, Loader2, DollarSign, Users, Building2, Info,
  TrendingUp, AlertTriangle, Target, ChevronDown, ChevronUp,
  Sparkles, ExternalLink, FileText, Calendar, Trophy, Crown,
  Medal, Mail, MessageSquareWarning,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, LineChart, Line,
} from 'recharts';
import CitizenContactModal from '../components/CitizenContactModal';
import LegislativeActivity from '../components/LegislativeActivity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBRL = (val) => {
  if (val == null || isNaN(val)) return 'N/A';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
};

const CHART_COLORS = ['#006030', '#F0D000', '#C62828', '#1565C0', '#6A1B9A', '#00838F', '#E65100', '#2E7D32', '#F57F17', '#4E342E'];

const GravidadeTag = ({ gravidade }) => {
  const map = { alta: 'bg-[#FFEBEE] text-[#C62828]', media: 'bg-[#FFF8E1] text-[#F57F17]', baixa: 'bg-[#E8F5E9] text-[#2E7D32]' };
  return <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${map[gravidade] || map.baixa}`}>{gravidade}</span>;
};

const Transparency = () => {
  const [searchName, setSearchName] = useState('');
  const [searchCargo, setSearchCargo] = useState('all');
  const [searchUF, setSearchUF] = useState('SP');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  const [selectedPolitician, setSelectedPolitician] = useState(null);
  const [selectedSource, setSelectedSource] = useState('');
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [expenses, setExpenses] = useState(null);
  const [selectedYear, setSelectedYear] = useState('2024');
  const [profile, setProfile] = useState(null);

  // Contato Cidadão
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactContext, setContactContext] = useState(null); // null = generic; {descricao, fornecedor, valor, data} = context

  const [interpretation, setInterpretation] = useState(null);
  const [loadingInterpret, setLoadingInterpret] = useState(false);
  const [showCategories, setShowCategories] = useState(true);
  const [showSuppliers, setShowSuppliers] = useState(false);
  const [showMonthly, setShowMonthly] = useState(false);

  // Ranking state — default: 2024 (latest year with data as of Ago/2026)
  const [ranking, setRanking] = useState(null);
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [rankingYear, setRankingYear] = useState('2024');

  // Load ranking on mount — auto-fallback to latest_year_with_data if empty
  useEffect(() => {
    const loadRanking = async () => {
      setLoadingRanking(true);
      try {
        const resp = await axios.get(`${API}/transparency/ranking`, {
          params: { ano: parseInt(rankingYear), limit: 10 }, timeout: 120000,
        });
        setRanking(resp.data);
        // Auto-jump to the latest year with data on first load (only if user hasn't manually changed)
        if (
          !resp.data.data_available &&
          resp.data.latest_year_with_data &&
          parseInt(rankingYear) > resp.data.latest_year_with_data
        ) {
          setRankingYear(String(resp.data.latest_year_with_data));
        }
      } catch (err) {
        console.error('Ranking load error:', err);
      }
      finally { setLoadingRanking(false); }
    };
    loadRanking();
  }, [rankingYear]);

  const handleSearch = useCallback(async () => {
    if (!searchName.trim()) { toast.error('Informe o nome do político.'); return; }
    setSearching(true);
    setSearchResults(null);
    setSelectedPolitician(null);
    setExpenses(null);
    setInterpretation(null);
    try {
      const cargoParam = searchCargo === 'all' ? '' : searchCargo;
      const isEstadual = searchCargo === 'deputado estadual';
      
      if (isEstadual) {
        // Search state deputies (any state)
        const resp = await axios.get(`${API}/transparency/estadual/deputados`, {
          params: { state: searchUF, nome: searchName.trim() }, timeout: 60000,
        });
        const deps = (resp.data.deputados || []).map(d => ({
          ...d, id: d.nome, partido: '', uf: d.uf || searchUF, foto: null,
        }));
        setSearchResults({ total: deps.length, results: { camara: [], senado: [], estadual: deps } });
      } else {
        const resp = await axios.get(`${API}/transparency/search`, {
          params: { name: searchName.trim(), cargo: cargoParam }, timeout: 20000,
        });
        const totalCount = (resp.data.results?.camara?.length || 0) + (resp.data.results?.senado?.length || 0);
        setSearchResults({ ...resp.data, total: resp.data.total ?? totalCount, results: { ...resp.data.results, estadual: [] } });
        if (totalCount === 0) toast.info('Nenhum parlamentar encontrado com este nome.');
      }
    } catch (err) {
      console.error('Search error:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Erro ao buscar parlamentar.';
      toast.error(detail);
    }
    finally { setSearching(false); }
  }, [searchName, searchCargo, searchUF]);

  const handleSelectPolitician = async (politician, source) => {
    setSelectedPolitician(politician);
    setSelectedSource(source);
    setExpenses(null);
    setInterpretation(null);
    setProfile(null);

    if (source === 'camara') {
      setLoadingExpenses(true);
      try {
        const [expResp, profResp] = await Promise.all([
          axios.get(`${API}/transparency/deputado/${politician.id}/despesas`, { params: { ano: parseInt(selectedYear) }, timeout: 30000 }),
          axios.get(`${API}/transparency/deputado/${politician.id}/perfil`, { timeout: 15000 }),
        ]);
        setExpenses(expResp.data);
        setProfile(profResp.data);
      } catch { toast.error('Erro ao carregar despesas.'); }
      finally { setLoadingExpenses(false); }
    } else if (source === 'estadual') {
      setLoadingExpenses(true);
      try {
        const resp = await axios.get(`${API}/transparency/estadual/despesas`, {
          params: { nome: politician.nome, state: politician.uf || 'SP', ano: parseInt(selectedYear) }, timeout: 60000,
        });
        setExpenses(resp.data);
        setProfile({ nome_parlamentar: politician.nome, partido: '', uf: politician.uf || 'SP', situacao: 'Em exercício' });
      } catch { toast.error('Erro ao carregar despesas estaduais.'); }
      finally { setLoadingExpenses(false); }
    }
  };

  const handleChangeYear = async (year) => {
    setSelectedYear(year);
    if (!selectedPolitician) return;
    setLoadingExpenses(true);
    setInterpretation(null);
    try {
      if (selectedSource === 'camara') {
        const resp = await axios.get(`${API}/transparency/deputado/${selectedPolitician.id}/despesas`, { params: { ano: parseInt(year) }, timeout: 30000 });
        setExpenses(resp.data);
      } else if (selectedSource === 'estadual') {
        const resp = await axios.get(`${API}/transparency/estadual/despesas`, {
          params: { nome: selectedPolitician.nome, state: selectedPolitician.uf || 'SP', ano: parseInt(year) }, timeout: 60000,
        });
        setExpenses(resp.data);
      }
    } catch { toast.error('Erro ao carregar despesas.'); }
    finally { setLoadingExpenses(false); }
  };

  const handleInterpret = async () => {
    if (!expenses?.resumo) return;
    setLoadingInterpret(true);
    try {
      const resp = await axios.post(`${API}/transparency/interpret`, {
        nome: selectedPolitician?.nome || '',
        cargo: 'Deputado(a) Federal',
        total_despesas: expenses.resumo.total || 0,
        por_categoria: expenses.resumo.por_categoria || [],
        top_fornecedores: expenses.resumo.top_fornecedores || [],
        alertas: expenses.alertas || [],
      }, { timeout: 50000 });
      setInterpretation(resp.data);
    } catch { toast.error('Erro ao gerar interpretação.'); }
    finally { setLoadingInterpret(false); }
  };

  const catData = expenses?.resumo?.por_categoria?.slice(0, 10).map(c => ({
    name: c.categoria?.length > 25 ? c.categoria.slice(0, 22) + '…' : c.categoria,
    value: c.total,
    fullName: c.categoria,
  })) || [];

  const monthData = expenses?.resumo?.por_mes?.map(m => ({
    name: m.mes,
    value: m.total,
  })) || [];

  const nivelColor = { alto: 'border-[#C62828] bg-[#FFEBEE]', medio: 'border-[#F57F17] bg-[#FFF8E1]', baixo: 'border-[#2E7D32] bg-[#E8F5E9]' };

  return (
    <div className="space-y-6" data-testid="transparency-page">
      <div className="text-center py-4 md:py-8">
        <h1 className="text-2xl sm:text-3xl lg:text-5xl font-black text-[#0F1C2E] tracking-tight mb-2" data-testid="transparency-title">
          Transparência Pública
        </h1>
        <p className="text-sm sm:text-base text-[#475569] max-w-2xl mx-auto px-2">
          Consulte despesas parlamentares, fornecedores e padrões de gastos com dados oficiais da Câmara dos Deputados.
        </p>
      </div>

      {/* Top 10 Ranking */}
      <Card className="p-4 sm:p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="ranking-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#F0D000]" />
            <h3 className="text-base font-bold text-[#0F1C2E]">Top 10: Maiores Gastos Parlamentares</h3>
          </div>
          <Select value={rankingYear} onValueChange={setRankingYear}>
            <SelectTrigger className="w-[90px] h-8 text-xs border-[#E2E8F0]" data-testid="ranking-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2026, 2025, 2024, 2023, 2022].map(y => <SelectItem key={y} value={String(y)}>{y}{y === 2026 ? ' ⭐' : ''}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loadingRanking && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-[#006030] mr-2" />
            <span className="text-sm text-[#94A3B8]">Consultando dados da Câmara dos Deputados…</span>
          </div>
        )}

        {!loadingRanking && ranking?.ranking?.length > 0 && (
          <>
            {ranking.stats && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-2 bg-[#F4F6F8] rounded-md">
                  <p className="text-[10px] text-[#94A3B8] uppercase">Deputados Analisados</p>
                  <p className="text-sm font-bold text-[#0F1C2E]">{ranking.stats.total_deputados}</p>
                </div>
                <div className="text-center p-2 bg-[#F4F6F8] rounded-md">
                  <p className="text-[10px] text-[#94A3B8] uppercase">Média de Gastos</p>
                  <p className="text-sm font-bold text-[#0F1C2E]">{formatBRL(ranking.stats.media_despesas)}</p>
                </div>
                <div className="text-center p-2 bg-[#FFEBEE] rounded-md">
                  <p className="text-[10px] text-[#C62828] uppercase">Maior Gasto</p>
                  <p className="text-sm font-bold text-[#C62828]">{formatBRL(ranking.stats.maior)}</p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {ranking.ranking.map((dep, idx) => {
                const medalColor = idx === 0 ? 'text-[#F0D000]' : idx === 1 ? 'text-[#94A3B8]' : idx === 2 ? 'text-[#CD7F32]' : 'text-[#E2E8F0]';
                const bgHighlight = idx < 3 ? 'bg-[#FAFBFC]' : '';
                return (
                  <div
                    key={dep.id}
                    onClick={() => { setSearchName(dep.nome); handleSelectPolitician(dep, 'camara'); setSearchResults({ total: 1, results: { camara: [dep], senado: [], estadual: [] } }); }}
                    className={`flex items-center gap-3 p-3 border border-[#E2E8F0] rounded-md cursor-pointer hover:border-[#006030] hover:shadow-sm transition-all ${bgHighlight}`}
                    data-testid={`ranking-item-${idx}`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center shrink-0">
                      {idx < 3 ? <Crown className={`w-5 h-5 ${medalColor}`} /> : <span className="text-sm font-bold text-[#94A3B8]">{idx + 1}</span>}
                    </div>
                    {dep.foto && <img src={dep.foto} alt="" className="w-9 h-9 rounded-full object-cover border border-[#E2E8F0] shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#0F1C2E] text-sm truncate">{dep.nome}</p>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className="text-[10px] border-[#006030] text-[#006030]">{dep.partido}/{dep.uf}</Badge>
                        <span className="text-[10px] text-[#94A3B8]">{dep.registros} despesas</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-black text-[#C62828]">{formatBRL(dep.total_despesas)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex items-start gap-2 text-[10px] text-[#94A3B8]">
              <Info className="w-3 h-3 shrink-0 mt-0.5" />
              <p>
                {ranking.disclaimer || 'Ranking baseado em despesas parlamentares registradas.'} Fonte: {ranking.fonte || 'Câmara dos Deputados'}.
                {ranking.cached && ' (dados em cache)'}
                {ranking.updated_at && ` Atualizado: ${new Date(ranking.updated_at).toLocaleDateString('pt-BR')}`}
              </p>
            </div>
          </>
        )}

        {!loadingRanking && (!ranking || ranking?.ranking?.length === 0) && (
          <div className="text-center py-8" data-testid="ranking-empty-state">
            <Trophy className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
            <p className="text-sm font-semibold text-[#0F1C2E] mb-2">
              Sem dados disponíveis para {rankingYear}
            </p>
            <p className="text-xs text-[#475569] max-w-md mx-auto leading-relaxed">
              {ranking?.message || `A Câmara dos Deputados atualiza esta base com defasagem. Os dados de ${rankingYear} podem ainda estar em processamento.`}
            </p>
            {ranking?.latest_year_with_data && ranking.latest_year_with_data !== parseInt(rankingYear) && (
              <Button
                onClick={() => setRankingYear(String(ranking.latest_year_with_data))}
                variant="outline"
                size="sm"
                className="mt-4 border-[#006030] text-[#006030] hover:bg-[#E8F5E9]"
                data-testid="ranking-fallback-btn"
              >
                Ver dados de {ranking.latest_year_with_data}
              </Button>
            )}
            <p className="text-[10px] text-[#94A3B8] mt-3">
              Fonte: {ranking?.fonte || 'Câmara dos Deputados — Dados Abertos'}
            </p>
          </div>
        )}
      </Card>

      {/* Search */}
      <Card className="p-4 sm:p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="transparency-search">
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-5 h-5 text-[#006030]" />
          <h3 className="text-base font-bold text-[#0F1C2E]">Buscar Parlamentar</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Nome</Label>
            <Input
              value={searchName} onChange={e => setSearchName(e.target.value)}
              placeholder="Ex.: Tabata Amaral" disabled={searching}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="mt-1 border-[#E2E8F0] focus:border-[#006030] focus:ring-[#006030]"
              data-testid="transparency-name-input"
            />
          </div>
          <div>
            <Label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Cargo</Label>
            <Select value={searchCargo} onValueChange={setSearchCargo}>
              <SelectTrigger className="mt-1 h-10 text-xs border-[#E2E8F0]" data-testid="transparency-cargo-select">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="deputado federal">Deputado Federal</SelectItem>
                <SelectItem value="deputado estadual">Deputado Estadual</SelectItem>
                <SelectItem value="senador">Senador</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {searchCargo === 'deputado estadual' && (
            <div>
              <Label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Estado</Label>
              <Select value={searchUF} onValueChange={setSearchUF}>
                <SelectTrigger className="mt-1 h-10 text-xs border-[#E2E8F0]" data-testid="transparency-uf-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'].map(uf => (
                    <SelectItem key={uf} value={uf}>{uf}{uf === 'SP' ? ' (com despesas)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <Button onClick={handleSearch} disabled={searching || !searchName.trim()} className="mt-4 bg-[#006030] hover:bg-[#004020] text-white font-medium h-10 px-6" data-testid="transparency-search-btn">
          {searching ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando…</> : <><Search className="w-4 h-4 mr-2" /> Buscar</>}
        </Button>
      </Card>

      {/* Results */}
      {searchResults && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="transparency-results">
          {/* Politician List */}
          <div className="lg:col-span-1">
            <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md">
              <h3 className="text-base font-bold text-[#0F1C2E] mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#006030]" /> Resultados ({searchResults.total})
              </h3>

              {searchResults.total === 0 && (
                <div className="text-center py-6" data-testid="no-results">
                  <Users className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
                  <p className="text-sm text-[#94A3B8]">Nenhum parlamentar encontrado.</p>
                  <p className="text-xs text-[#94A3B8] mt-1">Verifique o nome e tente novamente.</p>
                </div>
              )}

              {/* Câmara results */}
              {searchResults.results?.camara?.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider mb-2">Câmara dos Deputados</p>
                  <div className="space-y-2">
                    {searchResults.results.camara.map((p, idx) => (
                      <div key={p.id || idx} onClick={() => handleSelectPolitician(p, 'camara')}
                        data-testid={`camara-result-${idx}`}
                        className={`p-3 border rounded-md cursor-pointer transition-all hover:border-[#006030] hover:shadow-sm ${selectedPolitician?.id === p.id ? 'border-[#006030] bg-[#E8F5E9]' : 'border-[#E2E8F0]'}`}>
                        <p className="font-semibold text-[#0F1C2E] text-sm">{p.nome}</p>
                        <div className="flex gap-1.5 mt-1">
                          <Badge variant="outline" className="text-[10px] border-[#006030] text-[#006030]">{p.partido}/{p.uf}</Badge>
                          <Badge className="text-[10px] bg-[#E8F5E9] text-[#006030]">Deputado(a)</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Senado results */}
              {searchResults.results?.senado?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider mb-2">Senado Federal</p>
                  <div className="space-y-2">
                    {searchResults.results.senado.map((p, idx) => (
                      <div key={p.id || idx} onClick={() => handleSelectPolitician(p, 'senado')}
                        data-testid={`senado-result-${idx}`}
                        className={`p-3 border rounded-md cursor-pointer transition-all hover:border-[#006030] hover:shadow-sm ${selectedPolitician?.id === p.id ? 'border-[#006030] bg-[#E8F5E9]' : 'border-[#E2E8F0]'}`}>
                        <p className="font-semibold text-[#0F1C2E] text-sm">{p.nome}</p>
                        <div className="flex gap-1.5 mt-1">
                          <Badge variant="outline" className="text-[10px] border-[#006030] text-[#006030]">{p.partido}/{p.uf}</Badge>
                          <Badge className="text-[10px] bg-[#F3E5F5] text-[#6A1B9A]">Senador(a)</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Estadual results */}
              {searchResults.results?.estadual?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider mb-2">Deputados Estaduais (SP)</p>
                  <div className="space-y-2">
                    {searchResults.results.estadual.map((p, idx) => (
                      <div key={p.nome || idx} onClick={() => handleSelectPolitician(p, 'estadual')}
                        data-testid={`estadual-result-${idx}`}
                        className={`p-3 border rounded-md cursor-pointer transition-all hover:border-[#006030] hover:shadow-sm ${selectedPolitician?.nome === p.nome && selectedSource === 'estadual' ? 'border-[#006030] bg-[#E8F5E9]' : 'border-[#E2E8F0]'}`}>
                        <p className="font-semibold text-[#0F1C2E] text-sm">{p.nome}</p>
                        <div className="flex gap-1.5 mt-1">
                          <Badge className="text-[10px] bg-[#FFF8E1] text-[#F57F17]">Dep. Estadual</Badge>
                          <Badge variant="outline" className="text-[10px] border-[#94A3B8] text-[#475569]">{p.uf}</Badge>
                          {p.total_despesas > 0 && <span className="text-[10px] text-[#006030] font-medium">{formatBRL(p.total_despesas)}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Expense Dashboard */}
          <div className="lg:col-span-2 space-y-4">
            {!selectedPolitician && (
              <Card className="p-8 md:p-12 bg-white border border-[#E2E8F0] rounded-md text-center" data-testid="empty-dashboard">
                <DollarSign className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
                <h3 className="text-xl font-bold text-[#0F1C2E] mb-2">Selecione um Parlamentar</h3>
                <p className="text-sm text-[#475569]">Clique em um resultado para consultar despesas e dados de transparência.</p>
              </Card>
            )}

            {selectedPolitician && (
              <>
                {/* Profile Header */}
                <Card className="p-4 sm:p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="politician-profile">
                  <div className="flex items-start gap-4">
                    {(selectedPolitician.foto || profile?.foto) && (
                      <img src={selectedPolitician.foto || profile?.foto} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-[#E2E8F0]" />
                    )}
                    <div className="flex-1">
                      <h2 className="text-xl font-black text-[#0F1C2E]" data-testid="politician-name">{profile?.nome_parlamentar || selectedPolitician.nome}</h2>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <Badge className="bg-[#006030] text-white text-xs">{selectedPolitician.partido}/{selectedPolitician.uf}</Badge>
                        <Badge variant="outline" className="text-xs border-[#94A3B8] text-[#475569]">{selectedSource === 'camara' ? 'Deputado(a) Federal' : selectedSource === 'estadual' ? 'Deputado(a) Estadual' : 'Senador(a)'}</Badge>
                        {profile?.situacao && <Badge variant="outline" className="text-xs border-[#2E7D32] text-[#2E7D32]">{profile.situacao}</Badge>}
                      </div>
                      {profile?.escolaridade && <p className="text-xs text-[#94A3B8] mt-1">{profile.escolaridade}</p>}
                    </div>
                    {/* Year selector + Fale com Gabinete */}
                    <div className="shrink-0 flex flex-col gap-2">
                      <Select value={selectedYear} onValueChange={handleChangeYear}>
                        <SelectTrigger className="w-[90px] h-9 text-xs border-[#E2E8F0]" data-testid="year-selector">
                          <Calendar className="w-3 h-3 mr-1" /><SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2025, 2024, 2023, 2022, 2021, 2020].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {selectedSource === 'camara' && selectedPolitician?.id && (
                        <Button
                          size="sm"
                          onClick={() => { setContactContext(null); setContactModalOpen(true); }}
                          className="h-9 bg-[#006030] hover:bg-[#004D26] text-white text-xs"
                          data-testid="fale-gabinete-btn"
                        >
                          <Mail className="w-3 h-3 mr-1" /> Fale com o Gabinete
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>

                {loadingExpenses && (
                  <Card className="p-8 bg-white border border-[#E2E8F0] rounded-md text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-[#006030] mx-auto mb-2" />
                    <p className="text-sm text-[#94A3B8]">Consultando dados de transparência…</p>
                  </Card>
                )}

                {!loadingExpenses && expenses && (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="expense-summary">
                      <Card className="p-3 bg-[#006030] text-white rounded-md">
                        <p className="text-[10px] font-medium opacity-80 uppercase">Total Despesas</p>
                        <p className="text-lg font-black mt-1">{formatBRL(expenses.resumo?.total)}</p>
                        <p className="text-[10px] opacity-60">{selectedYear}</p>
                      </Card>
                      <Card className="p-3 bg-white border border-[#E2E8F0] rounded-md">
                        <p className="text-[10px] font-bold text-[#94A3B8] uppercase">Registros</p>
                        <p className="text-lg font-black text-[#0F1C2E] mt-1">{expenses.resumo?.total_registros || 0}</p>
                      </Card>
                      <Card className="p-3 bg-white border border-[#E2E8F0] rounded-md">
                        <p className="text-[10px] font-bold text-[#94A3B8] uppercase">Categorias</p>
                        <p className="text-lg font-black text-[#0F1C2E] mt-1">{expenses.resumo?.por_categoria?.length || 0}</p>
                      </Card>
                      <Card className="p-3 bg-white border border-[#E2E8F0] rounded-md">
                        <p className="text-[10px] font-bold text-[#94A3B8] uppercase">Fornecedores</p>
                        <p className="text-lg font-black text-[#0F1C2E] mt-1">{expenses.resumo?.top_fornecedores?.length || 0}</p>
                      </Card>
                    </div>

                    {/* Alerts */}
                    {expenses.alertas?.length > 0 && (
                      <div className="space-y-2" data-testid="expense-alerts">
                        {expenses.alertas.map((a, idx) => (
                          <div key={idx} className="p-3 border border-[#E2E8F0] rounded-md bg-white flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-[#F57F17] shrink-0 mt-0.5" />
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge className="text-[10px] bg-[#FFF8E1] text-[#F57F17]">{a.selo}</Badge>
                                <GravidadeTag gravidade={a.gravidade} />
                              </div>
                              <p className="text-xs text-[#475569] mt-1">{a.descricao}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* AI Interpretation */}
                    <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md" data-testid="interpretation-section">
                      {!interpretation ? (
                        <Button size="sm" onClick={handleInterpret} disabled={loadingInterpret} className="bg-[#006030] hover:bg-[#004020] text-white text-xs" data-testid="interpret-btn">
                          {loadingInterpret ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Analisando…</> : <><Sparkles className="w-3 h-3 mr-1" /> Interpretar com IA</>}
                        </Button>
                      ) : (
                        <div className={`p-3 rounded-md border ${nivelColor[interpretation.nivel_atencao] || nivelColor.baixo}`} data-testid="interpretation-result">
                          {interpretation.destaque && <p className="text-xs font-bold text-[#0F1C2E] mb-1">{interpretation.destaque}</p>}
                          <p className="text-xs text-[#475569] leading-relaxed">{interpretation.interpretacao}</p>
                          {interpretation.disclaimer && <p className="text-[10px] text-[#94A3B8] mt-2 italic">{interpretation.disclaimer}</p>}
                        </div>
                      )}
                    </Card>

                    {/* Categories Chart */}
                    <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md">
                      <button onClick={() => setShowCategories(!showCategories)} className="flex items-center gap-2 text-sm font-bold text-[#0F1C2E] hover:text-[#006030] transition-colors w-full text-left" data-testid="toggle-categories">
                        <DollarSign className="w-4 h-4 text-[#006030]" /> Despesas por Categoria
                        {showCategories ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                      </button>
                      {showCategories && catData.length > 0 && (
                        <ResponsiveContainer width="100%" height={Math.max(200, catData.length * 30)} className="mt-3">
                          <BarChart data={catData} layout="vertical" margin={{ left: 5, right: 15 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis type="number" tickFormatter={v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}k` : v} tick={{ fontSize: 10, fill: '#94A3B8' }} />
                            <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 10, fill: '#475569' }} />
                            <Tooltip formatter={v => [formatBRL(v), 'Valor']} contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 11 }} />
                            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
                              {catData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Card>

                    {/* Monthly Chart */}
                    <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md">
                      <button onClick={() => setShowMonthly(!showMonthly)} className="flex items-center gap-2 text-sm font-bold text-[#0F1C2E] hover:text-[#006030] transition-colors w-full text-left" data-testid="toggle-monthly">
                        <Calendar className="w-4 h-4 text-[#006030]" /> Evolução Mensal
                        {showMonthly ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                      </button>
                      {showMonthly && monthData.length > 0 && (
                        <ResponsiveContainer width="100%" height={250} className="mt-3">
                          <LineChart data={monthData} margin={{ left: 10, right: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                            <YAxis tickFormatter={v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}k` : v} tick={{ fontSize: 10, fill: '#94A3B8' }} />
                            <Tooltip formatter={v => [formatBRL(v), 'Total']} contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 11 }} />
                            <Line type="monotone" dataKey="value" stroke="#006030" strokeWidth={2} dot={{ fill: '#006030', r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </Card>

                    {/* Top Suppliers */}
                    <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md">
                      <button onClick={() => setShowSuppliers(!showSuppliers)} className="flex items-center gap-2 text-sm font-bold text-[#0F1C2E] hover:text-[#006030] transition-colors w-full text-left" data-testid="toggle-suppliers">
                        <Building2 className="w-4 h-4 text-[#006030]" /> Principais Fornecedores ({expenses.resumo?.top_fornecedores?.length || 0})
                        {showSuppliers ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                      </button>
                      {showSuppliers && (
                        <div className="mt-3 space-y-1.5">
                          {expenses.resumo?.top_fornecedores?.slice(0, 15).map((s, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs p-2 bg-[#FAFBFC] rounded border border-[#E2E8F0]" data-testid={`supplier-${idx}`}>
                              <div className="min-w-0 flex-1">
                                <p className="text-[#0F1C2E] font-medium truncate">{s.nome}</p>
                                {s.cnpj && <p className="text-[10px] text-[#94A3B8]">{s.cnpj}</p>}
                              </div>
                              <div className="text-right shrink-0 ml-3">
                                <p className="text-[#006030] font-bold">{formatBRL(s.total)}</p>
                                <p className="text-[10px] text-[#94A3B8]">{s.count}x</p>
                              </div>
                              {selectedSource === 'camara' && selectedPolitician?.id && (
                                <button
                                  onClick={() => {
                                    setContactContext({
                                      descricao: `gastos registrados junto a este fornecedor no ano de ${selectedYear}`,
                                      fornecedor: s.nome,
                                      valor: s.total,
                                      data: selectedYear,
                                    });
                                    setContactModalOpen(true);
                                  }}
                                  className="ml-2 shrink-0 p-1.5 rounded-full hover:bg-[#FFF8E1] text-[#8C5A00] transition-colors"
                                  title="Questionar este gasto"
                                  aria-label="Questionar este gasto"
                                  data-testid={`questionar-fornecedor-${idx}`}
                                >
                                  <MessageSquareWarning className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>

                    {/* Atuação Legislativa — só para Deputados Federais */}
                    {selectedSource === 'camara' && selectedPolitician?.id && (
                      <LegislativeActivity deputadoId={selectedPolitician.id} />
                    )}

                    {/* Source & LGPD */}
                    <div className="flex items-start gap-2 p-3 bg-[#F4F6F8] border border-[#E2E8F0] rounded-md" data-testid="transparency-disclaimer">
                      <Info className="w-4 h-4 text-[#94A3B8] shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[10px] text-[#94A3B8] leading-relaxed">
                          Dados obtidos de fontes públicas oficiais ({expenses.fonte || 'Câmara dos Deputados'}). Informações de caráter educativo. Padrões identificados não constituem evidência de irregularidade.
                        </p>
                        <p className="text-[10px] text-[#94A3B8] mt-1">
                          Atualizado em: {expenses.updated_at ? new Date(expenses.updated_at).toLocaleString('pt-BR') : 'N/A'}
                        </p>
                        <a href="https://dadosabertos.camara.leg.br/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-[#006030] hover:underline mt-1">
                          <ExternalLink className="w-3 h-3" /> Acessar Dados Abertos da Câmara
                        </a>
                      </div>
                    </div>
                  </>
                )}

                {!loadingExpenses && selectedSource === 'senado' && (
                  <Card className="p-8 bg-white border border-[#E2E8F0] rounded-md text-center" data-testid="senado-notice">
                    <Building2 className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-[#0F1C2E] mb-2">Dados do Senado</h3>
                    <p className="text-sm text-[#475569] mb-3">Despesas detalhadas de senadores estão disponíveis no portal de transparência do Senado Federal.</p>
                    <a href="https://www12.senado.leg.br/transparencia" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#006030] hover:underline">
                      <ExternalLink className="w-4 h-4" /> Acessar Portal de Transparência do Senado
                    </a>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de Contato e Cobrança Cidadã */}
      <CitizenContactModal
        open={contactModalOpen}
        onClose={() => { setContactModalOpen(false); setContactContext(null); }}
        deputadoId={selectedPolitician?.id}
        despesa={contactContext}
      />
    </div>
  );
};

export default Transparency;
